import { useMemo, useState, useEffect } from 'react'
import { BarChart3, TrendingDown, Users, Clock, Gauge, AlertTriangle, DollarSign, ArrowUpRight, ArrowDownRight, Info } from 'lucide-react'
import { SkeletonChart, SkeletonCard } from '@/components/ui/Skeleton'
import { useAuth } from '@/contexts/AuthContext'

interface ArrivalPoint { hour: number; guests: number; avg_spend: number; avg_scans: number; avg_duration_min: number; avg_attractions: number }
interface PlanType { plan_type: string; guests: number; avg_scans: number; avg_attractions: number; avg_duration_min: number; avg_spend: number; total_revenue: number }
interface ChannelRow { channel: string; channel_label: string; guests: number; avg_scans: number; avg_spend: number; avg_duration_min: number; avg_attractions: number; total_revenue: number }
interface MissedSavings { cheapest_unlimited_price: number; credit_guests_who_overspent: number; total_credit_guests: number; avg_credit_spend: number; total_overspend_dollars: number; overspend_pct: number }
interface CreditExhaustionPoint { hour: number; guests: number; avg_remaining: number; exhausted: number }
interface CreditExhaustion { points: CreditExhaustionPoint[]; total_credit_guests: number; total_exhausted: number; exhaustion_rate: number }
interface CapacityRide { name: string; historical_max: number; historical_avg: number; this_event_peak: number; headroom_pct: number; events_appeared: number }
interface CapacityCeiling { rides: CapacityRide[]; theoretical_ceiling: number; actual_peak: number; utilization_pct: number; ride_count: number }
interface BottleneckHour { hour: number; bottleneck_ride: string; scans: number; historical_max: number; utilization_pct: number }
interface DeadZone { name: string; type: string; dead_hours: number[] }
interface HourlyCurvePoint { hour: number; scans: number; revenue: number }

interface RoadmapData {
  missed_savings: MissedSavings | null
  plan_type_comparison: PlanType[]
  arrival_curve: { points: ArrivalPoint[]; total_guests: number; peak_arrival_hour: number }
  channel_comparison: ChannelRow[]
  credit_exhaustion: CreditExhaustion
  capacity_ceiling: CapacityCeiling
  bottleneck_timeline: BottleneckHour[]
  dead_zones: DeadZone[]
  type_hourly_curves: { Rides: HourlyCurvePoint[]; Games: HourlyCurvePoint[]; Other: HourlyCurvePoint[] }
}

function fmt(n: number): string { return n.toLocaleString() }
function fmtDollars(n: number): string { return `$${n.toLocaleString()}` }
function fmtHour(h: number): string {
  if (h === 0) return '12am'
  if (h < 12) return `${h}am`
  if (h === 12) return '12pm'
  return `${h - 12}pm`
}

function SectionHeader({ icon: Icon, title, subtitle }: { icon: typeof BarChart3; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-4 w-4 text-butler-600" />
      <h2 className="font-display font-semibold text-base text-ink-dark">{title}</h2>
      {subtitle && <span className="text-xs text-ink-soft">— {subtitle}</span>}
    </div>
  )
}

function StatCard({ label, value, sub, trend }: { label: string; value: string; sub?: string; trend?: 'up' | 'down' | null }) {
  return (
    <div className="mm-card-v3 p-4">
      <div className="text-xs text-ink-soft mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="font-display font-bold text-xl text-ink-dark">{value}</span>
        {trend === 'up' && <ArrowUpRight className="h-4 w-4 text-green-600" />}
        {trend === 'down' && <ArrowDownRight className="h-4 w-4 text-red-500" />}
      </div>
      {sub && <div className="text-xs text-ink-soft mt-1">{sub}</div>}
    </div>
  )
}

export function DeepAnalyticsPage() {
  const { currentEventId } = useAuth()
  const [data, setData] = useState<RoadmapData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!currentEventId) return
    setLoading(true)
    setError(null)
    fetch(`/api/dashboard/${currentEventId}/roadmap-analytics`, {
      credentials: 'same-origin',
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [currentEventId])

  const arrivalMax = useMemo(() => {
    if (!data?.arrival_curve?.points?.length) return 1
    return Math.max(...data.arrival_curve.points.map(p => p.guests), 1)
  }, [data])

  const capacityMax = useMemo(() => {
    if (!data?.capacity_ceiling?.rides?.length) return 1
    return Math.max(...data.capacity_ceiling.rides.map(r => r.historical_max), 1)
  }, [data])

  if (!currentEventId) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
            <BarChart3 className="h-5 w-5 text-butler-600" />
          </div>
          <h1 className="font-display font-bold text-xl text-ink-dark">Deep Analytics</h1>
        </div>
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-ink-soft">
          Select an event to see deep analytics.
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
          <BarChart3 className="h-5 w-5 text-butler-600" />
        </div>
        <div>
          <h1 className="font-display font-bold text-xl text-ink-dark">Deep Analytics</h1>
          <p className="text-sm text-ink-soft">Guest economics, capacity modeling & operational intelligence</p>
        </div>
      </div>

      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
          <SkeletonChart />
        </div>
      )}

      {error && (
        <div className="mm-card-v3 p-4 border-l-4 border-red-400 bg-red-50">
          <p className="text-sm text-red-700">Failed to load analytics: {error}</p>
        </div>
      )}

      {data && (
        <>
          {/* ── Missed Savings Hero ────────────────────────────────── */}
          {data.missed_savings && data.missed_savings.credit_guests_who_overspent > 0 && (
            <section>
              <SectionHeader icon={DollarSign} title="FastPass Missed Savings" subtitle="Credit guests who overspent" />
              <div className="mm-card-v3 p-5 border-l-4 border-amber-400 bg-amber-50/30">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <StatCard
                    label="Guests who overspent"
                    value={fmt(data.missed_savings.credit_guests_who_overspent)}
                    sub={`${data.missed_savings.overspend_pct}% of credit buyers`}
                  />
                  <StatCard
                    label="Total overspend"
                    value={fmtDollars(data.missed_savings.total_overspend_dollars)}
                    sub="Would have saved on FastPass"
                    trend="down"
                  />
                  <StatCard
                    label="Avg credit spend"
                    value={fmtDollars(data.missed_savings.avg_credit_spend)}
                    sub={`vs ${fmtDollars(data.missed_savings.cheapest_unlimited_price)} wristband`}
                  />
                  <StatCard
                    label="Total credit guests"
                    value={fmt(data.missed_savings.total_credit_guests)}
                    sub="Potential FastPass converts"
                  />
                </div>
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-100/60 rounded-md p-3">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    <strong>{fmt(data.missed_savings.credit_guests_who_overspent)}</strong> guests spent more on individual credits than the cheapest Unlimited wristband ({fmtDollars(data.missed_savings.cheapest_unlimited_price)}). These are prime conversion targets for FastPass upsell at the kiosk.
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* ── Plan Type Comparison ──────────────────────────────── */}
          {data.plan_type_comparison.length > 0 && (
            <section>
              <SectionHeader icon={Users} title="Guest Value by Plan Type" subtitle="Who engages more?" />
              <div className="mm-card-v3 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Plan Type</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Guests</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Avg Scans</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Avg Attractions</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Avg Duration</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Avg Spend</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.plan_type_comparison.map(p => (
                      <tr key={p.plan_type} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-3 px-4 text-sm font-medium text-ink-dark">{p.plan_type}</td>
                        <td className="py-3 px-4 text-right mm-data text-sm">{fmt(p.guests)}</td>
                        <td className="py-3 px-4 text-right mm-data text-sm">{p.avg_scans}</td>
                        <td className="py-3 px-4 text-right mm-data text-sm">{p.avg_attractions}</td>
                        <td className="py-3 px-4 text-right mm-data text-sm">{p.avg_duration_min}m</td>
                        <td className="py-3 px-4 text-right mm-data text-sm">{fmtDollars(p.avg_spend)}</td>
                        <td className="py-3 px-4 text-right mm-data text-sm font-semibold">{fmtDollars(p.total_revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Arrival Hour Revenue Curve ────────────────────────── */}
          {data.arrival_curve?.points?.length > 0 && (
            <section>
              <SectionHeader icon={Clock} title="Arrival Hour Economics" subtitle="When guests arrive vs what they spend" />
              <div className="mm-card-v3 p-5">
                <div className="space-y-2">
                  {data.arrival_curve.points.map(p => (
                    <div key={p.hour} className="flex items-center gap-3">
                      <span className="text-xs text-ink-soft w-12 text-right">{fmtHour(p.hour)}</span>
                      <div className="flex-1 flex gap-1 items-center">
                        <div className="h-5 bg-butler-400 rounded" style={{ width: `${Math.round(p.guests / arrivalMax * 100)}%`, minWidth: '2px' }} />
                        <span className="text-xs mm-data text-ink-soft">{fmt(p.guests)}</span>
                      </div>
                      <span className="text-xs mm-data text-ink-dark w-14 text-right font-semibold">{fmtDollars(p.avg_spend)}</span>
                      <span className="text-xs text-ink-soft w-12 text-right">{p.avg_scans} scans</span>
                      <span className="text-xs text-ink-soft w-14 text-right">{p.avg_duration_min}m</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-2 text-xs text-ink-soft bg-slate-50 rounded-md p-3 mt-4">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Early arrivals spend dramatically more. Peak volume is at {fmtHour(data.arrival_curve.peak_arrival_hour)} but peak spend per guest is earlier in the day.
                </div>
              </div>
            </section>
          )}

          {/* ── Channel Comparison ────────────────────────────────── */}
          {data.channel_comparison.length > 1 && (
            <section>
              <SectionHeader icon={TrendingDown} title="Online vs Kiosk Value" subtitle="Purchase channel comparison" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {data.channel_comparison.map(c => (
                  <div key={c.channel} className="mm-card-v3 p-4">
                    <div className="text-sm font-semibold text-ink-dark mb-2">{c.channel_label}</div>
                    <div className="text-2xl font-display font-bold text-butler-600 mb-1">{fmt(c.guests)}</div>
                    <div className="text-xs text-ink-soft">guests</div>
                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                      <div><span className="text-xs text-ink-soft">Avg spend</span><div className="text-sm mm-data font-semibold">{fmtDollars(c.avg_spend)}</div></div>
                      <div><span className="text-xs text-ink-soft">Avg scans</span><div className="text-sm mm-data font-semibold">{c.avg_scans}</div></div>
                      <div><span className="text-xs text-ink-soft">Duration</span><div className="text-sm mm-data">{c.avg_duration_min}m</div></div>
                      <div><span className="text-xs text-ink-soft">Attractions</span><div className="text-sm mm-data">{c.avg_attractions}</div></div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── Credit Exhaustion ─────────────────────────────────── */}
          {data.credit_exhaustion?.points?.length > 0 && (
            <section>
              <SectionHeader icon={TrendingDown} title="Credit Exhaustion" subtitle="When credit guests run out" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <StatCard label="Credit guests" value={fmt(data.credit_exhaustion.total_credit_guests)} />
                <StatCard label="Fully exhausted" value={fmt(data.credit_exhaustion.total_exhausted)} sub={`${data.credit_exhaustion.exhaustion_rate}% ran out completely`} />
                <StatCard label="Potential reload targets" value={fmt(data.credit_exhaustion.total_credit_guests - data.credit_exhaustion.total_exhausted)} sub="Left with unused credits" />
              </div>
              <div className="mm-card-v3 p-5">
                <div className="space-y-2">
                  {data.credit_exhaustion.points.map(p => {
                    return (
                      <div key={p.hour} className="flex items-center gap-3">
                        <span className="text-xs text-ink-soft w-12 text-right">{fmtHour(p.hour)}</span>
                        <div className="flex-1 flex gap-1 items-center">
                          <div className="h-5 bg-red-400 rounded" style={{ width: `${Math.round(p.exhausted / Math.max(...data.credit_exhaustion.points.map(x => x.exhausted), 1) * 100)}%`, minWidth: p.exhausted > 0 ? '2px' : '0' }} />
                        </div>
                        <span className="text-xs mm-data text-red-600 w-10 text-right">{p.exhausted}</span>
                        <span className="text-xs text-ink-soft w-20 text-right">{fmtDollars(p.avg_remaining)} left</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ── Capacity Ceiling ──────────────────────────────────── */}
          {data.capacity_ceiling?.rides?.length > 0 && (
            <section>
              <SectionHeader icon={Gauge} title="Theoretical Capacity Ceiling" subtitle="Historical max vs this event" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <StatCard label="Theoretical max/hr" value={fmt(data.capacity_ceiling.theoretical_ceiling)} sub={`${data.capacity_ceiling.ride_count} rides in roster`} />
                <StatCard label="Actual peak/hr" value={fmt(data.capacity_ceiling.actual_peak)} />
                <StatCard label="Utilization" value={`${data.capacity_ceiling.utilization_pct}%`} sub={data.capacity_ceiling.utilization_pct < 50 ? 'Significant headroom' : 'Running efficiently'} />
                <StatCard label="Unused capacity" value={fmt(data.capacity_ceiling.theoretical_ceiling - data.capacity_ceiling.actual_peak)} sub="scans/hr potential" />
              </div>
              <div className="mm-card-v3 p-5">
                <div className="space-y-2">
                  {data.capacity_ceiling.rides.slice(0, 15).map(r => (
                    <div key={r.name} className="flex items-center gap-3">
                      <span className="text-sm text-ink-dark w-36 truncate" title={r.name}>{r.name}</span>
                      <div className="flex-1 relative h-5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-slate-300 rounded-full" style={{ width: `${Math.round(r.historical_max / capacityMax * 100)}%` }} />
                        <div className={`absolute inset-y-0 left-0 rounded-full ${r.headroom_pct > 80 ? 'bg-red-400' : r.headroom_pct > 50 ? 'bg-amber-400' : 'bg-green-400'}`} style={{ width: `${Math.round(r.this_event_peak / capacityMax * 100)}%` }} />
                      </div>
                      <span className="text-xs mm-data w-12 text-right">{fmt(r.this_event_peak)}</span>
                      <span className="text-xs text-ink-soft w-14 text-right">/ {fmt(r.historical_max)}</span>
                      <span className={`text-xs w-10 text-right font-semibold ${r.headroom_pct > 80 ? 'text-red-600' : r.headroom_pct > 50 ? 'text-amber-600' : 'text-green-600'}`}>{r.headroom_pct}%</span>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-ink-soft mt-3">
                  Gray = historical max across all events. Color bar = this event&apos;s peak. Red &gt;80%, amber 50–80%, green &lt;50%.
                </div>
              </div>
            </section>
          )}

          {/* ── Bottleneck Timeline ───────────────────────────────── */}
          {data.bottleneck_timeline?.length > 0 && (
            <section>
              <SectionHeader icon={AlertTriangle} title="Hourly Bottleneck" subtitle="Which ride constrains the midway each hour" />
              <div className="mm-card-v3 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Hour</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Bottleneck Ride</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Scans</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Hist. Max</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Utilization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bottleneck_timeline.map(b => (
                      <tr key={b.hour} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-3 px-4 text-sm text-ink-dark">{fmtHour(b.hour)}</td>
                        <td className="py-3 px-4 text-sm font-medium text-ink-dark">{b.bottleneck_ride}</td>
                        <td className="py-3 px-4 text-right mm-data text-sm">{fmt(b.scans)}</td>
                        <td className="py-3 px-4 text-right mm-data text-sm text-ink-soft">{fmt(b.historical_max)}</td>
                        <td className="py-3 px-4 text-right text-sm">
                          <span className={`font-semibold ${b.utilization_pct > 80 ? 'text-red-600' : b.utilization_pct > 50 ? 'text-amber-600' : 'text-green-600'}`}>
                            {b.utilization_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Dead Zones ────────────────────────────────────────── */}
          {data.dead_zones?.length > 0 && (
            <section>
              <SectionHeader icon={AlertTriangle} title="Dead Zones" subtitle="Attraction-hour combos with zero activity" />
              <div className="mm-card-v3 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Attraction</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Type</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Idle Hours</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.dead_zones.map(d => (
                      <tr key={d.name} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-3 px-4 text-sm font-medium text-ink-dark">{d.name}</td>
                        <td className="py-3 px-4 text-sm text-ink-soft">{d.type}</td>
                        <td className="py-3 px-4 text-sm text-ink-soft">
                          {d.dead_hours.map(h => fmtHour(h)).join(', ')}
                        </td>
                        <td className="py-3 px-4 text-right mm-data text-sm text-red-600 font-semibold">{d.dead_hours.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Game vs Ride Hourly ───────────────────────────────── */}
          {data.type_hourly_curves?.Rides?.length > 0 && (
            <section>
              <SectionHeader icon={BarChart3} title="Revenue by Type & Hour" subtitle="Rides vs Games through the day" />
              <div className="mm-card-v3 p-5">
                <div className="space-y-2">
                  {(() => {
                    const allHours = new Set<number>()
                    data.type_hourly_curves.Rides.forEach(p => allHours.add(p.hour))
                    data.type_hourly_curves.Games.forEach(p => allHours.add(p.hour))
                    const hours = [...allHours].sort((a, b) => a - b)
                    const rideMap = new Map(data.type_hourly_curves.Rides.map(p => [p.hour, p]))
                    const gameMap = new Map(data.type_hourly_curves.Games.map(p => [p.hour, p]))
                    const maxRev = Math.max(...data.type_hourly_curves.Rides.map(p => p.revenue), 1)

                    return hours.map(h => {
                      const ride = rideMap.get(h)
                      const game = gameMap.get(h)
                      return (
                        <div key={h} className="flex items-center gap-3">
                          <span className="text-xs text-ink-soft w-12 text-right">{fmtHour(h)}</span>
                          <div className="flex-1">
                            <div className="flex gap-1 mb-0.5">
                              <div className="h-3 bg-butler-400 rounded" style={{ width: `${Math.round((ride?.revenue ?? 0) / maxRev * 100)}%`, minWidth: ride?.revenue ? '2px' : '0' }} title={`Rides: ${fmtDollars(ride?.revenue ?? 0)}`} />
                            </div>
                            <div className="flex gap-1">
                              <div className="h-3 bg-green-400 rounded" style={{ width: `${Math.round((game?.revenue ?? 0) / maxRev * 100)}%`, minWidth: game?.revenue ? '2px' : '0' }} title={`Games: ${fmtDollars(game?.revenue ?? 0)}`} />
                            </div>
                          </div>
                          <span className="text-xs mm-data text-butler-600 w-16 text-right">{fmtDollars(ride?.revenue ?? 0)}</span>
                          <span className="text-xs mm-data text-green-600 w-14 text-right">{fmtDollars(game?.revenue ?? 0)}</span>
                        </div>
                      )
                    })
                  })()}
                </div>
                <div className="flex gap-6 mt-3 text-xs text-ink-soft">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-butler-400 rounded" /> Rides</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded" /> Games</span>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
